'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { SystemNotice } from '@/components/murkoff/system-notice';
import { pushCrashLog } from '@/lib/crash-log';

interface SegmentErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// Route-segment boundary: this renders INSIDE RootLayout and Providers, so it
// only catches throws from the page tree below them. A throw in the layout or
// the provider stack is caught by src/app/global-error.tsx instead.
export default function SegmentError({ error, reset }: SegmentErrorProps) {
  const router = useRouter();
  // Persist the boundary trip into __crashLog before paint — useEffect runs
  // post-paint, and on a mobile reload the user often refreshes before the
  // effect fires, in which case the entry never hits localStorage.
  // useInsertionEffect, not the render body: pushCrashLog does a synchronous
  // JSON.stringify + localStorage.setItem of up to 50 events, and a render
  // React discards and replays would have been swallowed by the ref gate.
  const logged = React.useRef(false);
  React.useInsertionEffect(() => {
    if (logged.current) return;
    logged.current = true;
     
    console.error('Global application error:', error);
    try {
      pushCrashLog({
        t: Date.now(),
        type: 'boundary',
        message: error.message,
        digest: error.digest,
        stack: error.stack,
      });
    } catch {
      // Never let the crash logger take the crash screen down with it.
    }
  }, [error]);

  return (
    <SystemNotice
      title="Unstable System Detected"
      subtitle="Unexpected Failure in Trial Environment"
      accessStateLabel="ERROR"
      errorCode={error.digest ? `MK-SYS-500-${error.digest}` : 'MK-SYS-500'}
      division="Infrastructure Stability & Containment Division"
    >
      {/* The boundary swaps the view client-side; without an alert role the
          failure is silent for a screen reader, which stays wherever focus
          was. */}
      <div role="alert" className="space-y-4 text-sm text-muted-foreground">
        <p className="text-sm text-muted-foreground/90 leading-relaxed">
          An unrecoverable error occurred while rendering the current view. The active trial,
          directive, or console state may be inconsistent. Murkoff systems have suspended normal
          operation for this request.
        </p>

        <p className="text-xs text-muted-foreground leading-relaxed">
          If this issue persists, notify the trial administrator and include the error code above.
          Additional diagnostics may be available in the underlying server logs.
        </p>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Button
          variant="destructiveOutline"
          className="text-xs tracking-[0.2em] uppercase"
          onClick={() => reset()}
        >
          Attempt Recovery
        </Button>

        <Button
          className="bg-destructive hover:bg-destructive/90 text-xs tracking-[0.2em] uppercase rounded-md px-6"
          onClick={() => router.push('/')}
        >
          Return to main console
        </Button>
      </div>
    </SystemNotice>
  );
}
